import tokens from '@kluein/design-tokens';
import { Text } from '@mantine/core';

import TEST_IDS from 'test-ids';

import type { CountTextProps } from './CountText.types';
import type { ReactNode } from 'react';

const countTextStyles = {
  borderRadius: tokens.borderWidth.medium,
  padding: `${tokens.spacing.xxxsmall} ${tokens.spacing.xxsmall}`,
  lineHeight: 'normal',
};

// TODO: Remove grommetMarginToStyle once klue-ui List is migrated to Mantine.
// klue-ui's List component injects `margin: { left: 'auto' }` via cloneElement
// on rightSlot elements. This shim converts that to CSS margin styles.
function grommetMarginToStyle(
  margin: Record<string, string>,
): Record<string, string> {
  const result: Record<string, string> = {};
  if (margin.left) result.marginLeft = margin.left;
  if (margin.right) result.marginRight = margin.right;
  if (margin.top) result.marginTop = margin.top;
  if (margin.bottom) result.marginBottom = margin.bottom;
  if (margin.horizontal) {
    result.marginLeft = margin.horizontal;
    result.marginRight = margin.horizontal;
  }
  if (margin.vertical) {
    result.marginTop = margin.vertical;
    result.marginBottom = margin.vertical;
  }
  return result;
}

function CountText({
  children,
  compact = false,
  c = tokens.color.neutral.darkgrey.main,
  size = 'sm',
  style,
  margin,
  ...rest
}: CountTextProps & { margin?: Record<string, string> }): ReactNode {
  const value =
    children && ['string', 'number'].includes(typeof children)
      ? Number(children).toLocaleString('en-US')
      : children;
  const marginStyle =
    margin && typeof margin === 'object' ? grommetMarginToStyle(margin) : {};
  const styleObj =
    typeof style === 'object' && style !== null
      ? (style as Record<string, unknown>)
      : {};
  const { borderColor, ...remainingStyle } = styleObj;
  const resolvedBorderColor =
    (borderColor as string) ?? tokens.color.neutral.mediumgrey.main;
  const bd =
    resolvedBorderColor === 'transparent'
      ? 'none'
      : `${tokens.borderWidth.xsmall} solid ${resolvedBorderColor}`;
  const mergedStyle = { ...countTextStyles, ...marginStyle, ...remainingStyle };
  return (
    <Text
      component="span"
      data-test-id={TEST_IDS.countText}
      c={c}
      size={size}
      bd={bd}
      style={mergedStyle}
      {...rest}
    >
      {compact && children && typeof children === 'number'
        ? new Intl.NumberFormat('en-US', {
            maximumFractionDigits: 1,
            notation: 'compact',
          }).format(children)
        : value}
    </Text>
  );
}

export default CountText;
